import axios from "axios";

interface ErrorRecord {
	endpoint: string;
	status: number | string;
	timestamp: number;
}

// Lista monitorowanych endpointów
const MONITORED_ENDPOINTS = [
	"/baza_wiedzy",
	"/auth/login",
	"/events/categories",
	"/events",
] as const;

// Historia błędów w pamięci (maksymalnie 10 ostatnich)
let errorHistory: Array<ErrorRecord> = [];
const MAX_HISTORY_SIZE = 10;

/**
 * Wyciąga endpoint z pełnego URL
 * @param url - Pełny URL z requestu (np. "http://api.example.com/v1/baza_wiedzy?page=1")
 * @returns Endpoint path (np. "/v1/baza_wiedzy")
 */
export const extractEndpoint = (url: string | undefined): string | null => {
	if (!url) return null;

	try {
		const urlObj = new URL(url);
		// Zwracamy pathname bez query params
		return urlObj.pathname;
	} catch {
		// Jeśli URL jest względny (bez domeny), zwracamy go jak jest
		// Usuwamy query params jeśli są
		const pathname = url.split("?")[0];
		return pathname || null;
	}
};

/**
 * Sprawdza czy endpoint jest na liście monitorowanych
 */
export const isMonitoredEndpoint = (endpoint: string | null): boolean => {
	if (!endpoint) return false;
	return MONITORED_ENDPOINTS.some((monitored) => endpoint.includes(monitored));
};

/**
 * Sprawdza czy błąd jest błędem CORS
 * CORS errors w axios:
 * - Brak response
 * - message zawiera "Network Error" lub podobne
 * - code może być "ERR_NETWORK"
 */
export const isCorsError = (error: unknown): boolean => {
	if (!axios.isAxiosError(error)) return false;

	// Brak response to typowy znak CORS/network error
	if (!error.response && error.message) {
		const message = error.message.toLowerCase();
		return (
			message.includes("network error") ||
			message.includes("cors") ||
			error.code === "ERR_NETWORK"
		);
	}

	return false;
};

/**
 * Identyfikuje typ błędu API i zwraca odpowiedni status
 * @returns Status błędu (500, "CORS") lub null jeśli błąd nie jest monitorowany
 */
export const identifyMonitoredError = (
	error: unknown
): { status: number | string; type: "server" | "cors" } | null => {
	if (!axios.isAxiosError(error)) return null;

	// Sprawdzenie błędu 500
	if (error.response?.status === 500) {
		return { status: 500, type: "server" };
	}

	// Sprawdzenie błędu CORS
	if (isCorsError(error)) {
		return { status: "CORS", type: "cors" };
	}

	return null;
};

/**
 * Zapisuje błąd do historii
 */
export const recordError = (endpoint: string, status: number | string): void => {
	const errorRecord: ErrorRecord = {
		endpoint,
		status,
		timestamp: Date.now(),
	};

	errorHistory.push(errorRecord);

	// Ograniczamy historię do ostatnich MAX_HISTORY_SIZE błędów
	if (errorHistory.length > MAX_HISTORY_SIZE) {
		errorHistory = errorHistory.slice(-MAX_HISTORY_SIZE);
	}

	console.log(
		`[API Error Monitor] Recorded error: ${status} from ${endpoint}`,
		`(Total errors: ${errorHistory.length})`
	);
};

/**
 * Sprawdza czy ostatnie 2 błędy pochodzą z różnych endpointów
 */
export const shouldShowTemporaryIssuesWarning = (): boolean => {
	if (errorHistory.length < 2) return false;

	const lastError = errorHistory[errorHistory.length - 1];
	const secondLastError = errorHistory[errorHistory.length - 2];

	// TypeScript guard - po sprawdzeniu length >= 2 elementy na pewno istnieją
	if (!lastError || !secondLastError) return false;

	const differentEndpoints = lastError.endpoint !== secondLastError.endpoint;

	if (differentEndpoints) {
		console.log(
			`[API Error Monitor] Last 2 errors from different endpoints:`,
			`${secondLastError.endpoint} -> ${lastError.endpoint}`
		);
	}

	return differentEndpoints;
};

/**
 * Zwraca historię błędów (do debugowania)
 */
export const getErrorHistory = (): Array<ErrorRecord> => {
	return [...errorHistory];
};
